// Stub for Round 2.5 responsive-boundary contracts (#50 + #51).
//
// The mobile-vs-desktop boundary follows the legacy app's
// `@media (max-width: 768px)` contract: width <= 768 or a mobile user-agent
// gets the mobile surface. Centralised here so responsive specs do not spread
// raw 768 literals or ad-hoc UA regexes.
//
// Pure boolean predicate keeps unit tests independent from any Playwright
// or DOM wiring; the E2E specs call `resolveSurface(window.innerWidth)` from
// the page evaluate-block.
//
// Issue: #50 (mobile) ↔ #51 (desktop) ; Round 2.5 (Sub-agente R25-C).

export const MD_BREAKPOINT_PX = 768 as const;

export type Surface = "mobile" | "desktop";

const MOBILE_USER_AGENT_RE =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i;

export function resolveSurface(viewportWidth: number): Surface {
  if (!Number.isFinite(viewportWidth)) {
    throw new Error(
      `resolveSurface: viewportWidth must be finite, received ${String(viewportWidth)}`,
    );
  }
  if (viewportWidth < 0) {
    throw new Error(
      `resolveSurface: viewportWidth must be >=0, received ${viewportWidth}`,
    );
  }
  return viewportWidth > MD_BREAKPOINT_PX ? "desktop" : "mobile";
}

export function isMobileUserAgent(userAgent: string): boolean {
  return MOBILE_USER_AGENT_RE.test(userAgent);
}

/** Primary-pointer + digitizer traits, read from matchMedia/navigator (see
 *  deviceTraits.ts). Kept as plain data so the resolvers stay pure. */
export type PointerTraits = {
  readonly hasCoarsePrimaryPointer: boolean;
  readonly maxTouchPoints: number;
};

/**
 * Touch-first device = phone/tablet: mobile UA (Android/iPhone/iPad/…) OR a
 * coarse PRIMARY pointer plus a touch digitizer. Catches Android "Site para
 * computador"/DeX (UA spoofs Linux x86_64 but the pointer stays coarse) and
 * iPadOS 13+ (Macintosh UA, maxTouchPoints 5). Touchscreen laptops keep a
 * fine primary pointer → NOT touch-first. 04/07: Android sem cache recebeu o
 * shell desktop — este predicado é a raiz do gate "mobile SEMPRE".
 *
 * Example: `isTouchFirstDevice(navigator.userAgent, readPointerTraits())`.
 */
export function isTouchFirstDevice(
  userAgent: string | undefined,
  pointer: PointerTraits | undefined,
): boolean {
  if (userAgent && isMobileUserAgent(userAgent)) return true;
  if (!pointer) return false;
  return pointer.hasCoarsePrimaryPointer && pointer.maxTouchPoints > 0;
}

export function resolveSurfaceForEnvironment(input: {
  readonly viewportWidth: number;
  readonly userAgent?: string | undefined;
  readonly pointer?: PointerTraits | undefined;
}): Surface {
  if (isTouchFirstDevice(input.userAgent, input.pointer)) {
    return "mobile";
  }
  return resolveSurface(input.viewportWidth);
}

export function isMobileViewport(viewportWidth: number): boolean {
  return resolveSurface(viewportWidth) === "mobile";
}

export function isDesktopViewport(viewportWidth: number): boolean {
  return resolveSurface(viewportWidth) === "desktop";
}

// History pagination differs by surface: mobile renders 5 rows/page (room is
// scarce — see C50-PAGINACAO-5-5-10), desktop renders 10 rows/page (room
// is wide — see legacy `app/(dashboard)/wallet/_components/wallet-content.tsx:281`).
// The same predicate drives both sides; resolver consumes a viewport width
// and returns the canonical page size, so a single regression spec catches
// drift on either surface.
export const MOBILE_HISTORY_PAGE_SIZE = 5 as const;
export const DESKTOP_HISTORY_PAGE_SIZE = 10 as const;

export function resolveHistoryPageSize(viewportWidth: number): number {
  return resolveSurface(viewportWidth) === "mobile"
    ? MOBILE_HISTORY_PAGE_SIZE
    : DESKTOP_HISTORY_PAGE_SIZE;
}
